/**
 * Phase 277 — Public Website Telemetry Hook
 *
 * Captures a privacy-conscious view of how marketing-site visitors flow
 * across out-play.in:
 *   • Rotating session_id (sessionStorage; never persists across tabs/restarts).
 *   • Focus-aware time-spent counter — only ticks while the tab actually has
 *     focus, so background tabs don't inflate engagement numbers.
 *   • Detects pricing-page interactions to power the "Retargetable Pricing
 *     Drop-offs" KPI in the Super Admin Web Tracking console.
 *   • Pulls email/phone from AuthContext IF the visitor is already signed in.
 *   • Streams to POST /api/analytics/track via heartbeat (every 15s when active)
 *     + an unload beacon using navigator.sendBeacon for reliability.
 *
 * Public routes only — this hook short-circuits the moment any in-app
 * (authenticated) route is mounted so internal staff browsing isn't
 * polluting the marketing analytics collection.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'outplay_telemetry_sid';
const HEARTBEAT_MS = 15_000;
// Only treat these top-level segments as "marketing site" pages worth tracking.
// Authenticated /dashboard, /students, /finance, etc. are explicitly excluded.
const PUBLIC_PREFIXES = ['/', '/features', '/pricing', '/contact', '/about', '/login', '/signup', '/book-call'];

function generateSessionId() {
  return (
    'ws_' +
    Date.now().toString(36) +
    '_' +
    Math.random().toString(36).slice(2, 10)
  );
}

/**
 * Phase 283 — Identity Resolution helper.
 * Returns the current visitor's anonymous telemetry session_id (or null if
 * sessionStorage is blocked). Public forms forward this to backend mutation
 * endpoints so anonymous beacons can be merged with form-submitted PII.
 */
export function getTelemetrySessionId() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function isPublicMarketingPath(pathname) {
  if (!pathname) return false;
  // Exact "/" match
  if (pathname === '/') return true;
  return PUBLIC_PREFIXES.some(
    (p) => p !== '/' && (pathname === p || pathname.startsWith(p + '/'))
  );
}

export default function useWebTelemetry() {
  const location = useLocation();
  const { user } = useAuth();

  const sessionIdRef = useRef(null);
  const focusedRef = useRef(typeof document === 'undefined' ? true : !document.hidden);
  const secondsRef = useRef(0);
  const pagesRef = useRef([]); // array of strings, capped at 50
  const checkedPricingRef = useRef(false);
  const convertedRef = useRef(false);
  const heartbeatTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const lastSentSecondsRef = useRef(0);
  // Phase 284 — drift-to-pitch auto-trigger refs
  const pricingDwellSecondsRef = useRef(0);
  const maxScrollPctRef = useRef(0);
  const autoPitchFiredRef = useRef(false);

  // Build the payload from refs so it always reflects the latest state.
  const buildPayload = (override = {}) => ({
    session_id: sessionIdRef.current,
    email: user?.email || null,
    phone: user?.phone || null,
    pages_visited: pagesRef.current.slice(-50),
    time_spent_seconds: secondsRef.current,
    checked_pricing: checkedPricingRef.current,
    converted: convertedRef.current,
    page: window.location.pathname,
    ...override,
  });

  const send = (final = false) => {
    if (!sessionIdRef.current) return;
    // Phase 277 — Only beacon while the visitor is on a public marketing
    // route.  Authenticated /dashboard, /students, /super-admin/* etc. are
    // explicitly excluded so internal staff browsing never pollutes the
    // public-traffic dashboard.
    if (!isPublicMarketingPath(window.location.pathname)) return;
    // Skip if there's nothing new to report unless this is the final unload beacon.
    if (!final && secondsRef.current === lastSentSecondsRef.current) return;
    lastSentSecondsRef.current = secondsRef.current;

    const payload = buildPayload();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`;
    try {
      if (final && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        // keepalive lets the request survive a page transition that races
        // ahead of the response (modern browsers).
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
          credentials: 'omit',
        }).catch(() => {/* swallow — analytics must never break the UX */});
      }
    } catch (_) {
      /* best-effort */
    }
  };

  // === Session bootstrap (once per mount; survives route changes) ===
  useEffect(() => {
    let sid = null;
    try {
      sid = sessionStorage.getItem(STORAGE_KEY);
    } catch (_) {
      sid = null; // SSR / private mode
    }
    if (!sid) {
      sid = generateSessionId();
      try { sessionStorage.setItem(STORAGE_KEY, sid); } catch (_) { /* ignore */ }
    }
    sessionIdRef.current = sid;
  }, []);

  // === Focus-aware second tick + heartbeat sender ===
  useEffect(() => {
    const onVisibility = () => {
      focusedRef.current = !document.hidden;
    };
    const onPageHide = () => send(true);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onPageHide);

    tickTimerRef.current = window.setInterval(() => {
      if (focusedRef.current) {
        secondsRef.current += 1;
        // Phase 284 — count dwell separately while on /pricing
        if (window.location.pathname.startsWith('/pricing')) {
          pricingDwellSecondsRef.current += 1;
        }
      }
    }, 1000);

    // Phase 284 — track max scroll depth as a percentage. Fire-and-forget.
    const onScroll = () => {
      try {
        const docEl = document.documentElement;
        const scrolled = window.scrollY || window.pageYOffset || 0;
        const viewport = window.innerHeight || docEl.clientHeight || 0;
        const total = (docEl.scrollHeight || docEl.offsetHeight || 0) - viewport;
        if (total <= 0) return;
        const pct = Math.max(0, Math.min(100, Math.round(((scrolled + viewport) / (total + viewport)) * 100)));
        if (pct > maxScrollPctRef.current) {
          maxScrollPctRef.current = pct;
          // Threshold-based fire: 60s on /pricing + 80% scroll, single shot per session
          if (
            !autoPitchFiredRef.current
            && window.location.pathname.startsWith('/pricing')
            && pricingDwellSecondsRef.current >= 60
            && pct >= 80
          ) {
            autoPitchFiredRef.current = true;
            try {
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/auto-pitch-trigger`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  session_id: sessionIdRef.current,
                  dwell_seconds: pricingDwellSecondsRef.current,
                  scroll_pct: pct,
                  page: window.location.pathname,
                }),
                keepalive: true,
                credentials: 'omit',
              }).catch(() => {});
            } catch (_) { /* never break UX */ }
          }
        }
      } catch (_) { /* best-effort */ }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    heartbeatTimerRef.current = window.setInterval(() => send(false), HEARTBEAT_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('beforeunload', onPageHide);
      window.removeEventListener('scroll', onScroll);
      if (tickTimerRef.current) window.clearInterval(tickTimerRef.current);
      if (heartbeatTimerRef.current) window.clearInterval(heartbeatTimerRef.current);
      // Final flush.
      send(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === Route change: log the new page, set pricing/conversion flags ===
  useEffect(() => {
    const path = location.pathname || '/';
    if (!isPublicMarketingPath(path)) {
      // Internal staff route — pause tracking, don't pollute the feed.
      return;
    }
    // Push & cap.
    pagesRef.current = [...pagesRef.current, path].slice(-50);
    if (path === '/pricing' || path.startsWith('/pricing/')) {
      checkedPricingRef.current = true;
    }
    // Conversion proxy: a known-pricing-visit followed by /signup or
    // /login or any authenticated landing.  The actual auth state flip
    // is detected separately in the next effect below.
  }, [location.pathname]);

  // === Conversion detection — fires the instant the user becomes auth'd ===
  useEffect(() => {
    if (user?.id && checkedPricingRef.current) {
      convertedRef.current = true;
      // Push an immediate beacon so the conversion is captured even if the
      // user navigates away before the next heartbeat.
      send(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}
